import type { ComparisonOperator } from '@einhaus/project314-types';
import type { Option } from '@/components/UI/MultiSelect';
import { nanoid } from 'nanoid';
import type {
    ConditionFormState,
    EditorConditionNode,
    EditorGroupNode,
    EditorNode,
    ScreenCursor,
    ScreenEntityTypeFilter,
    ScreenerConditionInput,
    ScreenerConditionNode,
    ScreenerGroupInput,
    ScreenerGroupNode,
    ScreenerGroupOperator,
    ScreenUniversePageInput
} from '../types/screenerEditor';

// ── Constants ────────────────────────────────────────────────────────────────

export const entityTypeOptions: Option[] = [
    { value: 'Asset', label: 'Asset' },
    { value: 'Cryptocurrency', label: 'Cryptocurrency' },
    { value: 'AssetIndex', label: 'Asset Index' }
];

export const operatorOptions: { value: ComparisonOperator; label: string }[] = [
    { value: '=', label: '=' },
    { value: '!=', label: '!=' },
    { value: '>', label: '>' },
    { value: '<', label: '<' },
    { value: '>=', label: '>=' },
    { value: '<=', label: '<=' },
    { value: 'any', label: 'Any (IN)' },
    { value: 'empty', label: 'Empty' },
    { value: 'not_empty', label: 'Not Empty' }
];

export const defaultCondition: ConditionFormState = {
    featureKey: 'fundamentals.marketCap',
    operator: '>=',
    valueType: 'number',
    value: '1000000000',
    values: '',
    duration: '',
    lookbackBars: '',
    missingPolicy: 'failClosed'
};

// ── Tree creation helpers ────────────────────────────────────────────────────

export function createEditorCondition(patch?: Partial<ConditionFormState>): EditorConditionNode {
    return {
        type: 'condition',
        id: nanoid(10),
        ...defaultCondition,
        ...(patch ?? {})
    };
}

export function createEditorGroup(operator: ScreenerGroupOperator, children?: EditorNode[]): EditorGroupNode {
    return { type: 'group', id: nanoid(10), operator, children: children ?? [] };
}

// ── CSV / string helpers ─────────────────────────────────────────────────────

export function csvToStrings(input: string): string[] {
    return input
        .split(',')
        .map((v) => v.trim())
        .filter((v) => v.length > 0);
}

export function csvToNumbers(input: string): number[] {
    const parts = csvToStrings(input);
    const numbers: number[] = [];

    for (const p of parts) {
        const n = Number(p);
        if (!Number.isFinite(n)) return [];
        numbers.push(n);
    }

    return numbers;
}

export function normalizeSymbols(input: string): string[] {
    return csvToStrings(input).map((s) => s.toUpperCase());
}

// ── Server → Editor conversion ───────────────────────────────────────────────

export function conditionNodeToFormState(node: ScreenerConditionNode): ConditionFormState {
    const { valueType } = node;
    const isList = valueType === 'numberList' || valueType === 'stringList' || valueType === 'enumList';
    const valuesStr = isList && Array.isArray(node.value) ? node.value.join(', ') : '';
    const valueStr = !isList ? String(node.value ?? '') : '';

    return {
        featureKey: node.featureKey,
        operator: node.operator,
        valueType,
        value: valueStr,
        values: valuesStr,
        duration: node.duration ?? '',
        lookbackBars: node.lookbackBars != null ? String(node.lookbackBars) : '',
        missingPolicy: node.missingPolicy ?? 'failClosed'
    };
}

export function groupNodeToEditor(group: ScreenerGroupNode): EditorGroupNode {
    const isConditionNode = (c: ScreenerGroupNode | ScreenerConditionNode): c is ScreenerConditionNode => 'featureKey' in c;

    const children: EditorNode[] = group.children.map((child) => {
        if (isConditionNode(child)) return { type: 'condition', id: nanoid(10), ...conditionNodeToFormState(child) };
        return groupNodeToEditor(child);
    });

    return { type: 'group', id: nanoid(10), operator: group.operator, children };
}

export function extractAlwaysIncludeSymbols(
    rootGroup: ScreenerGroupNode
): { symbols: string[]; strippedRootGroup: ScreenerGroupNode } | null {
    const isConditionNode = (c: ScreenerGroupNode | ScreenerConditionNode): c is ScreenerConditionNode => 'featureKey' in c;
    const isGroupNode = (c: ScreenerGroupNode | ScreenerConditionNode): c is ScreenerGroupNode => !isConditionNode(c);

    if (rootGroup.operator !== 'or') return null;
    if (rootGroup.children.length !== 2) return null;

    const [a, b] = rootGroup.children;
    const aIsSymbolCond = isConditionNode(a) && a.featureKey === 'entity.symbol' && a.operator === 'any' && Array.isArray(a.value);
    const bIsSymbolCond = isConditionNode(b) && b.featureKey === 'entity.symbol' && b.operator === 'any' && Array.isArray(b.value);

    const symbolNode = aIsSymbolCond ? a : bIsSymbolCond ? b : null;
    const otherNode = aIsSymbolCond ? b : bIsSymbolCond ? a : null;

    if (!symbolNode || !otherNode) return null;
    if (!isGroupNode(otherNode)) return null;
    if (!Array.isArray(symbolNode.value)) return null;

    const symbols = symbolNode.value.filter((v): v is string => typeof v === 'string').map((s) => s.toUpperCase());
    if (symbols.length === 0) return null;

    return { symbols, strippedRootGroup: otherNode };
}

// ── Tree manipulation (immutable) ────────────────────────────────────────────

export function updateGroupAtPath(
    root: EditorGroupNode,
    path: number[],
    updater: (group: EditorGroupNode) => EditorGroupNode
): EditorGroupNode {
    if (path.length === 0) return updater(root);

    const [head, ...tail] = path;
    const child = root.children[head];
    if (child?.type !== 'group') return root;

    const updatedChild = updateGroupAtPath(child, tail, updater);
    return { ...root, children: root.children.map((c, idx) => (idx === head ? updatedChild : c)) };
}

export function updateNodeAtPath(root: EditorGroupNode, path: number[], updater: (node: EditorNode) => EditorNode): EditorGroupNode {
    if (path.length === 0) {
        const next = updater(root);
        return next.type === 'group' ? next : root;
    }

    const parentPath = path.slice(0, -1);
    const childIdx = path[path.length - 1];

    return updateGroupAtPath(root, parentPath, (group) => {
        const child = group.children[childIdx];
        if (!child) return group;
        const nextChild = updater(child);
        return { ...group, children: group.children.map((c, idx) => (idx === childIdx ? nextChild : c)) };
    });
}

export function removeNodeAtPath(root: EditorGroupNode, path: number[]): EditorGroupNode {
    if (path.length === 0) return root;
    const parentPath = path.slice(0, -1);
    const childIdx = path[path.length - 1];
    return updateGroupAtPath(root, parentPath, (group) => ({
        ...group,
        children: group.children.filter((_, idx) => idx !== childIdx)
    }));
}

// ── Editor → API input conversion ────────────────────────────────────────────

export function editorConditionToInput(c: EditorConditionNode): ScreenerConditionInput {
    const duration = c.duration.trim().length ? c.duration.trim() : null;
    const lookbackBars = c.lookbackBars.trim().length ? Number(c.lookbackBars.trim()) : null;

    const base = {
        type: 'condition' as const,
        featureKey: c.featureKey,
        operator: c.operator,
        duration,
        lookbackBars,
        missingPolicy: c.missingPolicy
    };

    if (c.valueType === 'number') {
        const n = Number(c.value);
        if (!Number.isFinite(n)) throw new Error(`Invalid number value for ${c.featureKey}`);
        return { ...base, valueType: 'number', value: n };
    }

    if (c.valueType === 'boolean') {
        const lower = c.value.trim().toLowerCase();
        const b = lower === 'true' ? true : lower === 'false' ? false : null;
        if (b == null) throw new Error(`Invalid boolean value for ${c.featureKey}`);
        return { ...base, valueType: 'boolean', value: b };
    }

    if (c.valueType === 'string' || c.valueType === 'enum') {
        return { ...base, valueType: c.valueType, value: c.value };
    }

    if (c.valueType === 'numberList') {
        const nums = csvToNumbers(c.values);
        if (!nums.length) throw new Error(`Invalid number list for ${c.featureKey}`);
        return { ...base, valueType: 'numberList', values: nums };
    }

    if (c.valueType === 'stringList' || c.valueType === 'enumList') {
        const vals = csvToStrings(c.values);
        if (!vals.length) throw new Error(`Invalid list values for ${c.featureKey}`);
        return { ...base, valueType: c.valueType, values: vals };
    }

    throw new Error('Unsupported valueType');
}

export function editorGroupToInput(g: EditorGroupNode): ScreenerGroupInput {
    const children = g.children.map((child) => {
        if (child.type === 'group') return editorGroupToInput(child);
        return editorConditionToInput(child);
    });

    if (children.length === 0) throw new Error('Each group must contain at least one child');

    return {
        type: 'group',
        operator: g.operator,
        children
    };
}

// ── Screen input builder ─────────────────────────────────────────────────────

export function buildScreenInput(config: {
    screenerRuleId: number;
    asOfTimestamp: number | undefined;
    entityTypeFilter: ScreenEntityTypeFilter;
    pageSize: number | undefined;
    cursor: ScreenCursor | null;
}): ScreenUniversePageInput {
    return {
        screenerRuleId: config.screenerRuleId,
        ...(config.asOfTimestamp !== undefined && config.asOfTimestamp !== 0 ? { asOfTimestamp: config.asOfTimestamp } : {}),
        ...(config.entityTypeFilter === 'all' ? {} : { entityType: config.entityTypeFilter }),
        ...(config.pageSize !== undefined ? { pageSize: config.pageSize } : {}),
        ...(config.cursor ? { cursor: config.cursor } : {})
    };
}

export function parseOptionalInt(
    raw: string,
    config: { label: string; min?: number; max?: number; allowZero?: boolean }
): { ok: true; value: number | undefined } | { ok: false; error: string } {
    const trimmed = raw.trim();
    if (!trimmed.length) return { ok: true, value: undefined };
    const n = Number(trimmed);
    if (!Number.isInteger(n)) return { ok: false, error: `${config.label} must be an integer` };
    if (!config.allowZero && n === 0) return { ok: false, error: `${config.label} must be > 0` };
    if (config.min !== undefined && n < config.min) return { ok: false, error: `${config.label} must be >= ${config.min}` };
    if (config.max !== undefined && n > config.max) return { ok: false, error: `${config.label} must be <= ${config.max}` };
    return { ok: true, value: n };
}

// ── CSV export helpers ───────────────────────────────────────────────────────

export function toCsv(rows: { entityType: string; entityId: number; symbol: string | null; name: string | null }[]): string {
    const escape = (value: string) => `"${value.replaceAll('"', '""')}"`;
    const lines = ['entityType,entityId,symbol,name'];

    for (const r of rows) {
        lines.push([escape(r.entityType), String(r.entityId), escape(r.symbol ?? ''), escape(r.name ?? '')].join(','));
    }

    return `${lines.join('\n')}\n`;
}

export function downloadTextFile(config: { filename: string; content: string; mimeType: string }) {
    const blob = new Blob([config.content], { type: config.mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = config.filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}
